import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UpdateContactAction, UpdateDocumentAction, BulkUpdateDocumentAction } from './actions'
import { Document, Tag } from '@/modules/document/types'
import { cloneDeep, set } from 'lodash'
import { ReactNodeLike } from 'prop-types'
import FileService from '@/modules/file/fileService'
import { OCR_FIELD_MAPPING } from '@/modules/document/constants/ocrFieldMapping'
import { handleApiError } from '@/redux/ducks/document/documentDrawerHelpers'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'

import { LIST_TYPES } from '@/redux/ducks/lists'
import { getAppState } from '@/redux/store'
import { fetchListAction } from '@/redux/ducks/lists/requests/documentList'
const { DOCUMENTS_LIST_TYPE } = LIST_TYPES
export interface DrawerTab {
  label: string,
  content: ReactNodeLike,
  disabled?: boolean,
  ocrContent?: boolean,
  actions?: ReactNodeLike,
}

interface ContactFormData {
  companyName: string,
  city: string,
  street: string,
  zipCode: string,
}

interface DrawerPage {
  current?: Document,
  next?: Document,
  previous?: Document,
  offset: number,
  count: number
}

interface ReceiptReviewAccessInfo {
  canRetryFailedAnalysis: boolean
}

export interface DocumentDrawerState {
  open: boolean,
  items: Array<Document>
  index: number,
  folderReadOnly: boolean,
  // FLAG:useDocuAi — server-computed, object-scoped Finance capability. This never turns the
  // drawer writable; it authorizes only the guarded terminal-analysis retry action.
  receiptReviewAccess: ReceiptReviewAccessInfo,
  analysisDone: boolean,
  rename: boolean,
  analyze: boolean,
  paymentDrawerOpen: boolean,
  availableOcrModalOpen: boolean,
  activeTab: number,
  analysisActive: boolean,
  isSaving: boolean,
  contactFormEnabled: boolean,
  document?: Document,
  initialDocument?: Document,
  contactModalOpen,
  contactFormData?: ContactFormData,
  isLoading: boolean,
  loadingPage: boolean,
  page: DrawerPage,
  // FLAG:useDocuAi — search parent-rollup: DMS version id of the matched child to
  // preselect in the preview switcher on drawer open. null = no preselect (Original).
  preselectChildVersionId?: string | null,
  // FLAG:useDocuAi — reload signal for the review component mounted in the drawer
  // (document-invoice-review-drawer-tab). Necessary because an action that changes the SAME document
  // server-side („In Prüfung zurücksetzen") touches neither `documentId` nor the component's local
  // nonce — its load effect would therefore never run again and the review would keep showing the
  // state from before the action. `fetchDocumentDetails` alone is insufficient: it updates the
  // drawer record, not the review component's own fetchFileInfo.
  reviewReloadNonce: number,
}

const initialState: DocumentDrawerState = {
  open: false,
  items: [],
  index: 0,
  folderReadOnly: false,
  receiptReviewAccess: { canRetryFailedAnalysis: false },
  rename: false,
  analyze: false,
  analysisDone: false,
  analysisActive: false,
  activeTab: 0,
  paymentDrawerOpen: false,
  availableOcrModalOpen: false,
  contactModalOpen: false,
  isSaving: false,
  isLoading: false,
  contactFormEnabled: false,
  page: {
    offset: 1,
    count: 1,
  },
  loadingPage: false,
  preselectChildVersionId: null,
  reviewReloadNonce: 0,
}

export interface FetchDocumentDetailsPayload {
  documentId: string
  analyze?: boolean
  rename?: boolean
  items?: Document[]
  index?: number
  analysisDone?: boolean
  analysisActive?: boolean
  activeTab?: number
  preserveTags?: Tag[]
  rowIndex?: number
  permissionRefresh?: boolean
  // FLAG:useDocuAi — matched-child preselect for the preview switcher.
  preselectChildVersionId?: string | null
}

interface FetchDocumentDetailsResult {
  document: Document
  initialDocument: Document
  isLoading: boolean
  folderReadOnly: boolean
  receiptReviewAccess: ReceiptReviewAccessInfo
  analyze?: boolean
  rename?: boolean
  items?: Document[]
  index?: number
}

interface FolderAccessInfo {
  canWrite?: boolean
}

interface DocumentInfoResponse {
  data: Document
  folderAccess?: FolderAccessInfo
  receiptReviewAccess?: ReceiptReviewAccessInfo
}

interface ApiError {
  body: {
    message: string
    name: string
    meta: object
  }
}

const isAccessRevokedError = (error?: ApiError) => {
  const meta = error?.body?.meta as Record<string, Array<{ code?: string | number }>> | undefined
  if (!meta) return false

  const fileCode = meta.file?.some(({ code }) => Number(code) === 403)
  const forbiddenId = meta.id?.some(({ code }) => code === 'NOT_ALLOWED')

  return Boolean(fileCode || forbiddenId)
}

export const fetchDocumentDetails = createAsyncThunk<FetchDocumentDetailsResult, FetchDocumentDetailsPayload, { rejectValue: { error: ApiError } }>(
  'documentDrawer/fetchDocumentDetails',
  async(action, { rejectWithValue }) => {
    const { documentId, preserveTags, rowIndex, permissionRefresh: _permissionRefresh, preselectChildVersionId, ...rest } = action
    try {
      const { data: document, folderAccess, receiptReviewAccess }: DocumentInfoResponse = await FileService.fetchDocumentInfo(documentId)
      const initialDocument = document
      const folderReadOnly = !(folderAccess?.canWrite ?? true)
      const scopedReceiptReviewAccess = {
        canRetryFailedAnalysis: receiptReviewAccess?.canRetryFailedAnalysis === true,
      }

      const updatedDocument = preserveTags?.length
        ? {
          ...document,
          tags: [...preserveTags, ...document.tags.filter((tag) => !tag.isSystem)],
        }
        : document

      const newState = {
        document: updatedDocument,
        initialDocument,
        folderReadOnly,
        receiptReviewAccess: scopedReceiptReviewAccess,
        isLoading: false,
        page: {},
        // FLAG:useDocuAi — always emitted (null when absent) so a plain drawer open
        // clears a previous search-open's preselect (the fulfilled reducer skips
        // undefined values).
        preselectChildVersionId: preselectChildVersionId ?? null,
        ...rest,
      }

      if (rowIndex || rowIndex === 0) {
        const { meta, filter } = getAppState().lists[DOCUMENTS_LIST_TYPE]
        const count = meta[filter]
        newState.page = {
          count,
          offset: rowIndex + 1,
        }
      }

      return newState
    } catch (error) {
      return rejectWithValue({ error })
    }
  },
)

const fetchDrawerDocument = async(documentId: string) => {
  const { data, folderAccess, receiptReviewAccess }: DocumentInfoResponse = await FileService.fetchDocumentInfo(documentId)
  return {
    ...data,
    folderAccess,
    receiptReviewAccess,
  }
}

const prepareNavigation = async(documentId, step, dispatch) => {
  const {
    document: {
      list: {
        items,
        pagination: { offset, limit, count },
      },
    },
  } = getAppState()

  const currentIndex = items.findIndex(({ id }) => id === documentId)
  if (currentIndex === -1) {
    throw new Error('Document not found in list')
  }

  // at the start or end of the items array, fetch next/previous list page if possible
  if (currentIndex === 0 && offset > 0 && step < 1) {
    await dispatch(fetchListAction({
      offset: Math.max(0, offset - limit),
      limit,
      clear: false,
    }))
  } else if (currentIndex === items.length - 1 && (offset + items.length) < count && step > -1) {
    await dispatch(fetchListAction({
      offset: offset + items.length,
      limit,
      clear: false,
    }))
  }

  return currentIndex
}

const doNavigation = async(step, currentIndex, items, current) => {
  switch (step) {
    case 0:
      return {
        previous: currentIndex > 0 ? await fetchDrawerDocument(items[currentIndex - 1].id) : null,
        next: currentIndex < items.length - 1 ? await fetchDrawerDocument(items[currentIndex + 1].id) : null,
      }
    case 1:
      return {
        previous: current,
        next: currentIndex < items.length - 1 ? await fetchDrawerDocument(items[currentIndex + 1].id) : null,
      }
    case -1:
      return {
        previous: currentIndex > 0 ? await fetchDrawerDocument(items[currentIndex - 1].id) : null,
        next: current,
      }
  }
}

interface DrawerPageParam {
  step: number, // 0 = initial load, 1 = next, -1 = previous
  documentId: string,
}
export const fetchDrawerPage = createAsyncThunk<DrawerPage, DrawerPageParam, { rejectValue: { error: ApiError } }>(
  'documentDrawer/fetchDrawerPage',
  async({ step, documentId }, { rejectWithValue, dispatch }) => {
    try {
      // get next index and maybe fetch more items
      const currentIndex = await prepareNavigation(documentId, step, dispatch)

      const {
        document: {
          list: { pagination: { count }, items },
          drawer,
        },
      } = getAppState()

      // fetch document info for next and previous (if available)
      const { previous, next } = await doNavigation(step, currentIndex, items, drawer.page.current)
      const current = drawer.document
        ? {
          ...drawer.document,
          folderAccess: { canWrite: !drawer.folderReadOnly },
          receiptReviewAccess: drawer.receiptReviewAccess,
        }
        : null

      return {
        current,
        previous,
        next,
        offset: currentIndex + 1,
        count,
      }
    } catch (error) {
      return rejectWithValue({ error })
    }
  },
)

const documentDrawerSlice = createSlice({
  name: 'documentDrawer',
  initialState,
  reducers: {
    closeDocumentDrawer() {
      return initialState
    },
    applyChanges(state) {
      return { ...state, initialDocument: state.document }
    },
    openWithPage(state, action) {
      const { offset, analyze, rename, activeTab, analysisDone } = action.payload
      const page = { ...action.payload.page, offset }
      const pageCurrent = page.current as Document & {
        folderAccess?: FolderAccessInfo
        receiptReviewAccess?: ReceiptReviewAccessInfo
      } | undefined
      const canWrite = pageCurrent?.folderAccess?.canWrite
      const folderReadOnly = !(canWrite ?? true)
      const receiptReviewAccess = {
        canRetryFailedAnalysis: pageCurrent?.receiptReviewAccess?.canRetryFailedAnalysis === true,
      }
      const pageCurrentWithoutAccess = { ...pageCurrent }
      delete pageCurrentWithoutAccess.folderAccess
      delete pageCurrentWithoutAccess.receiptReviewAccess

      const newState = {
        ...state,
        open: true,
        loadingPage: false,
        folderReadOnly,
        receiptReviewAccess,
        document: pageCurrentWithoutAccess as Document,
        initialDocument: pageCurrentWithoutAccess as Document,
        page,
      }

      if (!state.open) {
        newState.analyze = analyze
        newState.rename = rename
        newState.activeTab = activeTab
        newState.analysisDone = analysisDone
      }

      return newState
    },
    setDrawerPage(state, action) {
      const { offset } = action.payload
      const page = { ...action.payload.page, offset }

      return {
        ...state,
        loadingPage: false,
        page,
      }
    },
    setDrawerDocument(state, action) {
      const document = action.payload
      const kindCurrent = state.document.tags.find(({ isSystem }) => !!isSystem)
      const kind = document.tags.find(({ isSystem }) => !!isSystem)
      const canWrite = (document as Document & { folderAccess?: FolderAccessInfo })?.folderAccess?.canWrite
      const folderReadOnly = !(canWrite ?? true)
      const receiptReviewAccess = {
        canRetryFailedAnalysis: (document as Document & { receiptReviewAccess?: ReceiptReviewAccessInfo })
          ?.receiptReviewAccess?.canRetryFailedAnalysis === true,
      }
      const documentWithoutAccess = { ...document } as Document & {
        folderAccess?: FolderAccessInfo
        receiptReviewAccess?: ReceiptReviewAccessInfo
      }
      delete documentWithoutAccess.folderAccess
      delete documentWithoutAccess.receiptReviewAccess

      const activeTab = kindCurrent?.label !== kind?.label ? 0 : state.activeTab // we actually have files without kind. e.g. signature certificates...

      return {
        ...state,
        folderReadOnly,
        receiptReviewAccess,
        document: documentWithoutAccess as Document,
        initialDocument: documentWithoutAccess as Document,
        activeTab,
        analyze: false,
        rename: false,
        analysisDone: false,
        loadingPage: true,
        // FLAG:useDocuAi — paging to another document clears the matched-child preselect.
        preselectChildVersionId: null,
      }
    },
    updateDocument(state, action: PayloadAction<UpdateDocumentAction>) {
      if (!state.document) return
      const { value, key } = action.payload
      const newDocument = cloneDeep(state.document)

      // Update the field value
      set(newDocument, key, value)

      // If the field is being emptied and has a corresponding OCR field
      if ((value === null || value === '' || value === 0) && OCR_FIELD_MAPPING[key as keyof typeof OCR_FIELD_MAPPING]) {
        const ocrField = OCR_FIELD_MAPPING[key as keyof typeof OCR_FIELD_MAPPING]
        // Create a new metaData object with the OCR field cleared
        newDocument.metaData = {
          ...newDocument.metaData,
          ocr: {
            ...newDocument.metaData?.ocr,
            [ocrField]: null,
          },
        }
      }

      state.document = newDocument
    },
    bulkUpdateDocument(state, action: PayloadAction<BulkUpdateDocumentAction>) {
      if (!state.document) return
      const { updates, metaData } = action.payload
      const newDocument = cloneDeep(state.document)

      updates.forEach(({ key, value }) => {
        set(newDocument, key, value)

        if ((value === null || value === '' || value === 0) && OCR_FIELD_MAPPING[key as keyof typeof OCR_FIELD_MAPPING]) {
          const ocrField = OCR_FIELD_MAPPING[key as keyof typeof OCR_FIELD_MAPPING]
          newDocument.metaData = {
            ...newDocument.metaData,
            ocr: {
              ...newDocument.metaData?.ocr,
              [ocrField]: null,
            },
          }
        }
      })

      if (metaData) {
        newDocument.metaData = {
          ...newDocument.metaData,
          ...metaData,
        }
      }

      state.document = newDocument
    },
    putDocument(state, action: PayloadAction<Document>) {
      return { ...state, document: action.payload }
    },
    updateDocumentKind(state, action: PayloadAction<Tag>) {
      const tags = [action.payload, ...state.document.tags.filter(({ isSystem }) => !isSystem)]

      const previousKind = state.document.tags.find(({ isSystem }) => !!isSystem)
      return { ...state, document: { ...state.document, tags }, activeTab: previousKind.label !== action.payload.label ? 0 : state.activeTab }
    },
    updateItem(state, action: PayloadAction<Document>) {
      const i = state.items.findIndex(({ id }) => id === action.payload.id)
      const newItems = cloneDeep(state.items)
      newItems[i] = action.payload
      return { ...state, items: newItems }
    },
    updateMultipleItems(state, action: PayloadAction<Document[]>) {
      return { ...state, items: action.payload }
    },
    updateContact(state, action: PayloadAction<UpdateContactAction>) {
      const { data, type } = action.payload
      const updated = { ...state.document }
      delete updated.payee

      switch (type) {
        case 'customer':
          updated.supplier = null
          updated.customer = { ...data, id: data.id ?? data.baseDataId }
          break
        case 'supplier':
          updated.customer = null
          updated.supplier = { ...data, id: data.id ?? data.baseDataId }
          break
        default:
      }

      return { ...state, document: { ...state.document, ...updated } }
    },
    setAnalysisActive(state, action: PayloadAction<boolean>) {
      return { ...state, analysisActive: action.payload }
    },
    setAnalysisDone(state, action: PayloadAction<boolean>) {
      return { ...state, analysisDone: action.payload }
    },
    setTab(state, action: PayloadAction<number>) {
      return { ...state, activeTab: action.payload }
    },
    openPaymentDrawer(state) {
      return { ...state, paymentDrawerOpen: true }
    },
    closePaymentDrawer(state) {
      return { ...state, paymentDrawerOpen: false }
    },
    openAvailableOcrModal(state) {
      return { ...state, availableOcrModalOpen: true }
    },
    closeAvailableOcrModal(state) {
      return { ...state, availableOcrModalOpen: false }
    },
    openCustomerSupplierModal(state, action: PayloadAction<ContactFormData>) {
      return { ...state, contactModalOpen: true, contactFormData: action.payload }
    },
    closeCustomerSupplierModal(state) {
      return { ...state, contactModalOpen: false }
    },
    setIsSaving(state, action: PayloadAction<boolean>) {
      return { ...state, isSaving: action.payload }
    },
    setContactFormEnabled(state, action: PayloadAction<boolean>) {
      return { ...state, contactFormEnabled: action.payload }
    },
    // FLAG:useDocuAi — „reload the review for this document". Deliberately the counter rather than
    // a flag: two consecutive calls must trigger two loads, while a boolean would already be set on
    // the second call. Dispatched only by actions that change the review state server-side; no one
    // dispatches them with flag-OFF, so the counter remains 0.
    requestReviewReload(state) {
      return { ...state, reviewReloadNonce: state.reviewReloadNonce + 1 }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentDetails.pending, (state) => {
        state.open = true
        state.isLoading = true
        // Fail closed while the requested object's scoped capability is still unknown. Otherwise
        // a previous terminal row could lend its retry button to the next document for one frame.
        state.receiptReviewAccess = { canRetryFailedAnalysis: false }
      })
      .addCase(fetchDocumentDetails.fulfilled, (state, action) => {
        Object.entries(action.payload)
          .filter(([_, value]) => value !== undefined)
          .forEach(([key, value]) => {
            state[key] = value
          })
      })
      .addCase(fetchDocumentDetails.rejected, (state, action) => {
        const error = action.payload?.error
        const isPermissionRefresh = action.meta.arg?.permissionRefresh

        if (isPermissionRefresh && isAccessRevokedError(error)) {
          toast.showToast({ type: 'info', message: lang.documents.errors.accessRevoked })
        } else {
          handleApiError(error?.body, lang.documents.errors.documentDetailsFetch)
        }

        state.isLoading = false
        state.document = null
        state.initialDocument = null
        state.folderReadOnly = false
        state.receiptReviewAccess = { canRetryFailedAnalysis: false }
        state.open = false
      })
      .addCase(fetchDrawerPage.pending, (state) => {
        state.loadingPage = true
      })
      .addCase(fetchDrawerPage.fulfilled, (state, { payload: page }) => {
        state.page = page
        state.loadingPage = false
      })
      .addCase(fetchDrawerPage.rejected, (state, action) => {
        handleApiError(action.payload.error?.body, lang.documents.errors.drawerPageFetch, true)
        state.loadingPage = false
      })
  },
})

export const {
  closeDocumentDrawer,
  setDrawerDocument,
  updateDocument,
  bulkUpdateDocument,
  putDocument,
  updateDocumentKind,
  updateItem,
  updateMultipleItems,
  updateContact,
  setAnalysisActive,
  setAnalysisDone,
  setTab,
  openPaymentDrawer,
  closePaymentDrawer,
  openAvailableOcrModal,
  closeAvailableOcrModal,
  openCustomerSupplierModal,
  closeCustomerSupplierModal,
  setIsSaving,
  applyChanges,
  openWithPage,
  setDrawerPage,
  setContactFormEnabled,
  requestReviewReload,
} = documentDrawerSlice.actions

export default documentDrawerSlice.reducer
